import type {Key} from 'react';
import type {ProjectResponse, WorkspaceTreeNode} from '@/types/api';
import type {WorkspaceDataNode} from '@/types/workspace/tree.types';
import {canEditContent} from '@/utils/workspace/projectRole';

export function projectKey(id: number): string {
	return `project:${id}`;
}

export function folderKey(id: number): string {
	return `folder:${id}`;
}

export function diagramKey(id: number): string {
	return `diagram:${id}`;
}

export function toProjectNode(project: ProjectResponse): WorkspaceDataNode {
	return {
		key: projectKey(project.id),
		title: project.name,
		nodeType: 'project',
		projectId: project.id,
		entityId: project.id,
		role: project.role,
		shared: Boolean(project.shared ?? (project.role && project.role !== 'OWNER')),
		description: project.description,
		isLeaf: false,
	};
}

export function toChildNode(node: WorkspaceTreeNode, role?: WorkspaceDataNode['role']): WorkspaceDataNode {
	const isFolder = node.type === 'FOLDER';
	return {
		key: isFolder ? folderKey(node.id) : diagramKey(node.id),
		title: node.name,
		nodeType: isFolder ? 'folder' : 'diagram',
		projectId: node.projectId,
		entityId: node.id,
		role,
		shared: Boolean(role && role !== 'OWNER'),
		isLeaf: node.leaf,
	};
}

export function updateTreeData(
	list: WorkspaceDataNode[],
	key: Key,
	children: WorkspaceDataNode[]
): WorkspaceDataNode[] {
	return list.map(node => {
		if (node.key === key) {
			return {...node, children};
		}
		if (node.children) {
			return {...node, children: updateTreeData(node.children, key, children)};
		}
		return node;
	});
}

export function mapTree(
	list: WorkspaceDataNode[],
	mapper: (node: WorkspaceDataNode) => WorkspaceDataNode
): WorkspaceDataNode[] {
	return list.map(node => {
		const next = mapper(node);
		if (next.children) {
			return {...next, children: mapTree(next.children, mapper)};
		}
		return next;
	});
}

export function removeNode(list: WorkspaceDataNode[], key: Key): WorkspaceDataNode[] {
	return list
		.filter(node => node.key !== key)
		.map(node => (node.children ? {...node, children: removeNode(node.children, key)} : node));
}

export function findNode(list: WorkspaceDataNode[], key: Key): WorkspaceDataNode | undefined {
	for (const node of list) {
		if (node.key === key) return node;
		if (node.children) {
			const found = findNode(node.children, key);
			if (found) return found;
		}
	}
	return undefined;
}

export function appendChild(
	list: WorkspaceDataNode[],
	parentKey: Key,
	child: WorkspaceDataNode
): WorkspaceDataNode[] {
	return list.map(node => {
		if (node.key === parentKey) {
			return {...node, children: [...(node.children ?? []), child], isLeaf: false};
		}
		if (node.children) {
			return {...node, children: appendChild(node.children, parentKey, child)};
		}
		return node;
	});
}

export function findParent(
	list: WorkspaceDataNode[],
	key: Key
): WorkspaceDataNode | undefined {
	for (const node of list) {
		if (node.children?.some(child => child.key === key)) {
			return node;
		}
		if (node.children) {
			const found = findParent(node.children, key);
			if (found) return found;
		}
	}
	return undefined;
}

export function isDescendant(root: WorkspaceDataNode, key: Key): boolean {
	return Boolean(root.children?.some(child => child.key === key || isDescendant(child, key)));
}

export function canDropOn(dragNode: WorkspaceDataNode, dropNode: WorkspaceDataNode, dropPosition: number): boolean {
	if (dragNode.nodeType === 'project' || !canEditContent(dragNode.role) || !canEditContent(dropNode.role)) return false;
	if (dragNode.projectId !== dropNode.projectId) return false;
	if (dropNode.nodeType === 'project' && dropPosition !== 0) return false;
	if (dropNode.nodeType === 'diagram' && dropPosition === 0) return false;
	if (dragNode.nodeType === 'folder' && (dropNode.key === dragNode.key || isDescendant(dragNode, dropNode.key))) {
		return false;
	}
	return true;
}

export function resolveDropParent(
	treeData: WorkspaceDataNode[],
	dragNode: WorkspaceDataNode,
	dropNode: WorkspaceDataNode,
	dropToGap: boolean
): WorkspaceDataNode | undefined {
	if (!canDropOn(dragNode, dropNode, dropToGap ? 1 : 0)) return undefined;
	if (dropToGap) {
		if (dropNode.nodeType === 'project') return undefined;
		const parent = findParent(treeData, dropNode.key);
		if (
			dragNode.nodeType === 'folder' &&
			parent &&
			(parent.key === dragNode.key || isDescendant(dragNode, parent.key))
		) {
			return undefined;
		}
		return parent;
	}
	if (dropNode.nodeType === 'diagram') {
		return findParent(treeData, dropNode.key);
	}
	return dropNode;
}

export function relocateNode(
	list: WorkspaceDataNode[],
	dragKey: Key,
	destParentKey: Key,
	destLoaded: boolean
): WorkspaceDataNode[] {
	const node = findNode(list, dragKey);
	if (!node) return list;
	const without = removeNode(list, dragKey);
	if (!destLoaded) return without;
	return appendChild(without, destParentKey, node);
}
